import { useLang } from 'hooks/lang';

import { actions, BUG_REPORT_SEVERITIES, bugReportStatuses, loaders } from 'entities';

import { Box, Button, Card, Column, IconButton, Modal, Row, Tag } from 'components/Blocks';
import { BugIcon, ChevronRightIcon, LinkIcon, SendIcon } from 'components/Icons';
import { MultiLineText } from 'components/MultiLineText';
import { AttachedFiles } from 'components/Uploads';
import { TextareaWithFiles } from 'components/Inputs';
import { UserAvatar } from 'components/UserAvatar';

import { useFileInput, useFormControlV2, useToggleV2 } from 'hooks';

import { emptyFn } from 'utils/function';
import { getFirst, getLastItem, listRemove } from 'utils/array';
import { cn } from 'styles';

const MIN_LENGTH = 6;

export function ForceBugContextModal() {
  const { t } = useLang();

  const [expanded, { toggle }] = useToggleV2();

  const fileInput = useFileInput();

  const [{ comment }, { handleChange }] = useFormControlV2({
    comment: '',
  });

  const sendIsDisabled = comment.length <= MIN_LENGTH;

  const { data, overwrite } = loaders.feedback.myContextNeededReports.use({
    defaultValue: [],

    refreshMinutes: 2,
  });

  const sendContext = actions.feedback.sendBugReportContext.use({
    block: sendIsDisabled,

    params: {
      comment,
      files: fileInput.files.map((f) => f.file),
      reportId: getFirst(data)?.id,
    },

    onSuccess: (_, { reportId }) => overwrite((old) => listRemove(old, reportId)),
  });

  if (!data.length) return null;

  const [{ description, link, title, severity, files, messages }] = data;

  const urgencyConfig = BUG_REPORT_SEVERITIES.find((s) => s.value === severity)!;

  const lastMessage = getLastItem(messages);

  return (
    <Modal
      isOpen
      close={emptyFn}
      showCloseButton={false}
      preventOpenAutoFocus={false}
      width="min(90%, 560px)"
      className="overflow-y-auto"
    >
      <Box className="mx-auto size-16 rounded-full border-primary bg-primary/20 p-2">
        <BugIcon size={36} color="primary-light" />
      </Box>

      <Column gap="1" alignItems="center">
        <h2 className="mx-auto text-center text-3xl text-primary">{t('context-is-needed')}</h2>

        <p className="text-center text-muted-foreground/50">{t('your-bug-report-needs-context')}</p>
      </Column>

      <Card flow="column" spacing="small">
        <Row gap="2">
          <IconButton onClick={toggle} size="small">
            <ChevronRightIcon
              className={cn('transition-transform', expanded && 'rotate-90')}
              size={14}
            />
          </IconButton>

          <h5 className="-ml-1 text-lg">{title}</h5>

          {!!link && (
            <IconButton href={link} linkTarget={link} size="small">
              <LinkIcon size={14} />
            </IconButton>
          )}

          <Tag name={urgencyConfig.getLabel(t)} color={urgencyConfig.color} type="outlined" />
        </Row>

        {expanded && (
          <>
            <Column gap="1">
              <MultiLineText
                text={description}
                renderLine={(line) => (
                  <p className="max-w-[95ch] text-justify text-sm leading-relaxed">{line}</p>
                )}
              />
            </Column>

            <AttachedFiles files={files} />
          </>
        )}
      </Card>

      {lastMessage?.status?.to === bugReportStatuses.needsContext.code && (
        <Card flow="column">
          {lastMessage.text && <p className="text-sm italic">{lastMessage.text}</p>}

          <UserAvatar
            user={{ ...lastMessage, id: lastMessage.userId }}
            name
            size={28}
            nameClassName="text-sm"
          />
        </Card>
      )}

      <TextareaWithFiles
        {...fileInput}
        label={t('your-context')}
        onChange={handleChange}
        value={comment}
        name="comment"
      />

      <Button {...sendContext.buttonProps} disabled={sendIsDisabled}>
        <SendIcon />
        {t('send')}
      </Button>
    </Modal>
  );
}
